import type { BadgeTone } from '@zarya/ui';
import { t } from '@app/i18n';
import type { ApiData, ApiQuery } from '@app/api/typed';
import type { components } from '@app/api/types';

export type AuditEntry = components['schemas']['AuditEntry'];
export type AuditEntryDetail = components['schemas']['AuditEntryDetail'];
export type AuditQuery = NonNullable<ApiQuery<'/api/v1/audit', 'get'>>;
export type EntityKind = NonNullable<AuditQuery['entity_kind']>;
export type ResultFilter = NonNullable<AuditQuery['result']>;

/** Ver `UsersPagina`: a interseção com `Paginacao` derruba o tipo do item. */
export type AuditPage = Omit<ApiData<'/api/v1/audit'>, 'items'> & { items: AuditEntry[] };

// aim-57a.9 (12/10/2026): o enum `entity_kind` do contrato, em inglês e com todos os tipos que o backend grava. A
// migração traduziu as linhas já gravadas, e o filtro não aceita mais os nomes antigos.
export const ENTITY_KINDS: readonly EntityKind[] = [
  'identity',
  'release',
  'approval',
  'contract',
  'resource',
  'tool',
  'policy',
  'user',
  'tenant',
  'config',
  'config_middleware',
  'discovery',
  'runtime',
  'admission',
  'pdp',
  'site',
  'environment',
  'decision',
  'session',
  // aim-57a.7 — o papel de identidade (`role.create`, `role.update`, `role.delete`). Sem ele no enum do contrato,
  // o filtro por tipo respondia 400 para as linhas de papel.
  'role',
];

/**
 * Catálogo de ações de `data-model.md › RegistroDeAuditoria`. É sugestão de
 * filtro, não validação: o combobox aceita digitar outra (`creatable`), porque
 * quem define a lista real é o backend.
 */
export const ACTIONS_KNOWN: readonly string[] = [
  'identity.assign',
  'identity.remove',
  'contract.approve',
  'contract.reject',
  'contract.auto_approve',
  // aim-gmh (12/10/2026) — a avaliacao gravada numa pendencia que espera uma pessoa, quando ela muda.
  'approval.record_evaluation',
  'contract.retry',
  // 29/08 — a plataforma fechando, junto de uma aprovação, as pendências de
  // builds anteriores ao aprovado.
  'contract.supersede',
  // 12/09 — a outra metade do que uma aprovacao fecha: as pendencias que o
  // contrato novo ja cobre. Entra junto de `contract.supersede` porque quem filtra
  // «o que esta aprovacao fechou?» precisa achar as duas; ate hoje esta fechava
  // sem linha nenhuma, e o filtro devolvia metade do que aconteceu.
  //
  // O prefixo e `approval.`, EN, e nao `contrato.`: verbo novo nasce em ingles
  // (regra de 04/09). Nao colide com namespace de i18n — a varredura do
  // `screenCatalog.test.ts` casa `<namespace>.`, e os vizinhos sao `approvals` e
  // `approvalWorkflow`, nenhum deles seguido de ponto depois de `approval`.
  'approval.close_covered',
  // aim-gmh (12/10/2026) — a ingestao grava no contrato as delegacoes A2A que ele perdeu na emissao. Muda a autoridade
  // do contrato, e ate aqui mudava sem linha na trilha. Prefixo `contract.`, no SINGULAR: o namespace de i18n e
  // `contracts`, e a varredura do `screenCatalog.test.ts` casa `<namespace>.`.
  'contract.delegations_complete',
  'catalog.save',
  'resource.delete',
  // Fase 2 (aim-co1.3) — o recurso agente: a sincronização dos agentes de um site (a leitura do gateway que cria,
  // atualiza e marca «sem rota») e o «Religar a uma identidade». Prefixo `resource.`, como a exclusão: quem filtra
  // «o que mudou neste recurso?» acha as três.
  'resource.agents_sync',
  'resource.agent_relink',
  'policy.publish',
  'policy.save_draft',
  'config.save',
  'config.test',
  'tenant.save',
  // aim-57a.7 — a criação de papel, por T21 e pela atribuição ou declaração de uma identidade com um papel novo.
  'role.create',
  'user.create',
  'user.deactivate',
  'discovery.run',
  // aim-gmh (12/10/2026) — a release nova que a varredura do registry grava, uma linha por release.
  'release.discover',
  'admission.reconcile',
  'pdp.republish',
  'deployment.apply',
  // 13/09 (P28) — o MESMO patch, disparado pela aprovacao num ambiente que sobe
  // sozinho. Entra ao lado da irma porque quem filtra «quem mandou esta imagem
  // para o ar?» precisa achar as duas, e a resposta muda conforme qual apareceu.
  'deployment.auto_apply',
  'session.login',
  'session.logout',
  // aim-gmh (12/10/2026) — o vinculo da conta Google na primeira entrada com Google, na transacao da sessao.
  'user.google_link',
  // P1 (09/09) — o cadastro de sites. `site.posture_change` ja existia no backend
  // desde a P5 e faltava aqui; entra junto, pelo mesmo motivo das quatro: quem
  // filtra a trilha por «o que mudou na infra?» precisa achar as cinco.
  //
  // O prefixo e `site.`, no SINGULAR. O namespace de i18n da tela e `sites`, no
  // plural, e a distincao NAO e estetica: a varredura do `screenCatalog.test.ts`
  // e larga e nao distingue `site.create` (acao) de `site.page.title` (catalogo).
  // Ver o doc de SCREEN_NAMESPACES — foi a mesma armadilha que fez a T15 virar
  // `runtimeStatus` e a T21 virar `tenantSettings`.
  'site.posture_change',
  'site.create',
  'site.update',
  'site.delete',
  'site.test_connection',
  // aim-xsk.8 — a admissão no cluster de cada Site. Entram juntas pelo filtro «o que mudou na admissão deste
  // cluster?»: o token, o registro, cada versão aplicada, o envio que falhou, o reenvio e a leitura do estado.
  'admission.token_create',
  'admission.token_revoke',
  'admission.register',
  'admission.config_apply',
  'admission.config_delivery',
  'admission.config_resend',
  'admission.status_read',
];

/**
 * Rótulo do resultado (FR-111): **sucesso** · `falha · {código}` ·
 * `falha · tempo esgotado` · `falha · parcial` · `falha · commit`.
 *
 * `parcial` é o único que não é vermelho: o middleware pode ter deixado estado
 * atrás, e a ação certa é reconciliar, não repetir.
 */
/** Códigos com frase própria; o resto sai como o backend mandou. */
const CODES_WITH_LABEL = ['tempo_esgotado', 'parcial', 'commit'];

export interface ResultView {
  text: string;
  tom: BadgeTone;
}

export function resultView(input: Pick<AuditEntry, 'result' | 'code'>): ResultView {
  if (input.result === 'sucesso') return { text: t('audit.result.success'), tom: 'success' };
  const codigo = input.code ?? t('audit.result.code.unknown');
  return {
    text: t('audit.result.failure', {
      code: CODES_WITH_LABEL.includes(codigo) ? t(`audit.result.code.${codigo}`) : codigo,
    }),
    tom: codigo === 'parcial' ? 'warning' : 'danger',
  };
}

/**
 * Rota do console para a entidade do registro.
 *
 * O detalhe traz `link` pronto do servidor (FR-111) — este mapa é para a linha
 * da grade, que não o recebe. Painel aberto é `?drawer=<id>` na rota da tela,
 * como manda a convenção do console.
 */
export function entityLink(tipo: string, id?: string | null): string | null {
  const withDrawer: Record<string, string> = {
    identity: '/identidades',
    approval: '/aprovacoes',
    contract: '/contratos',
    resource: '/resources',
    // `/access-control/human`, não `/politicas`: é o que o servidor devolve em
    // `link` (usecases/audit/links.go) — a linha da grade e o detalhe do mesmo
    // registro levavam a telas diferentes.
    policy: '/access-control/human',
    user: '/usuarios',
  };
  const withoutID: Record<string, string> = {
    release: '/identidades',
    tool: '/resources',
    tenant: '/tenant',
    config: '/infra',
    discovery: '/tenant',
    runtime: '/runtime',
    // aim-57a.7: o mesmo `link` que o servidor devolve no detalhe (usecases/audit/links.go): os papéis se gerem em
    // Configurações do tenant (T21).
    role: '/tenant',
    // aim-57a.9: os tipos que o backend já gravava e o enum não tinha, com o mesmo `link` do servidor.
    config_middleware: '/infra',
    admission: '/runtime',
    pdp: '/runtime',
    site: '/sites',
    environment: '/tenant',
    decision: '/decisoes',
  };

  const rota = withDrawer[tipo];
  if (rota) return id ? `${rota}?drawer=${id}` : rota;
  return withoutID[tipo] ?? null;
}

export interface AuditFilterValues {
  /** `yyyy-mm-dd` do `<input type="date">`; vazio = sem limite. */
  desde: string;
  ate: string;
  user: string;
  entity_kind: EntityKind | null;
  action: string | null;
  result: ResultFilter | null;
  page: number;
}

export const FILTERS_INICIAIS: AuditFilterValues = {
  desde: '',
  ate: '',
  user: '',
  entity_kind: null,
  action: null,
  result: null,
  page: 1,
};

export function hasFilterApplied(filters: AuditFilterValues): boolean {
  return Boolean(
    filters.desde ||
    filters.ate ||
    filters.user.trim() ||
    filters.entity_kind ||
    filters.action ||
    filters.result,
  );
}

/**
 * `yyyy-mm-dd` → instante local em RFC 3339, que é o que o contrato aceita.
 * O período é inclusive nas duas pontas: o fim do dia entra inteiro.
 */
function instant(data: string, fimDoDia: boolean): string | undefined {
  if (!data) return undefined;
  const local = new Date(`${data}T${fimDoDia ? '23:59:59.999' : '00:00:00.000'}`);
  if (Number.isNaN(local.getTime())) return undefined;
  return local.toISOString();
}

export function forQuery(filters: AuditFilterValues): AuditQuery {
  const desde = instant(filters.desde, false);
  const ate = instant(filters.ate, true);
  const usuario = filters.user.trim();

  return {
    ...(desde ? { desde } : {}),
    ...(ate ? { ate } : {}),
    ...(usuario ? { user: usuario } : {}),
    ...(filters.entity_kind ? { entity_kind: filters.entity_kind } : {}),
    ...(filters.action ? { action: filters.action } : {}),
    ...(filters.result ? { result: filters.result } : {}),
    page: filters.page,
    // Mais recente primeiro: a auditoria é lida de trás para frente.
    sort: '-when',
  };
}
